const formatDate = (value) => {
  if (!value) return '';
  const date = new Date(`${String(value).slice(0, 10)}T00:00:00`);
  return Number.isNaN(date.getTime()) ? String(value) : date.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
};

export const deriveConfirmationLetterScheduleData = ({ auditSchedules = [], scope = '' } = {}) => {
  const dates = [...new Set(auditSchedules.flatMap((schedule) => {
    const details = Array.isArray(schedule?.mandayDetails) ? schedule.mandayDetails.map((row) => row?.date) : [];
    return details.filter(Boolean).length ? details : [schedule?.startDate, schedule?.endDate].filter(Boolean);
  }))].sort();
  const auditType = auditSchedules.find((schedule) => schedule?.auditType)?.auditType || 'On-site';
  return { auditType, conductedOn: dates.map(formatDate).filter(Boolean).join(', '), scope: String(scope || '') };
};

// Placeholder the stage/scope mapping uses for "no value" -- treat it as empty here so the
// FSCP-03 Scope box is left blank (and thus re-prefillable) rather than filled with "-".
const NO_SCOPE_PLACEHOLDER = '-';

const cleanScopeText = (value) => {
  const text = String(value ?? '').trim();
  return !text || text === NO_SCOPE_PLACEHOLDER ? '' : text;
};

/**
 * Resolves the proposal scope text for a scheduling scope key.
 *
 * `schedulingScopes[]` objects carry `key`/`label`/`standardId` but never a `scope` field --
 * reading `scope` off them (as the FSCP-03 prefill used to) always yielded ''. The real scope
 * text lives on the matching `stages[]` row, which is what the stage card renders. Falls back
 * to the scheduling scope's own label when the stage carries no scope text of its own.
 */
export const resolveScopeTextForScopeKey = ({ stages = [], schedulingScopes = [], scopeKey = '' } = {}) => {
  if (!scopeKey) return '';
  const stageScope = cleanScopeText(stages.find((stage) => stage?.key === scopeKey)?.scope);
  if (stageScope) return stageScope;
  return cleanScopeText(schedulingScopes.find((scope) => scope?.key === scopeKey)?.label);
};

/**
 * Merges derived prefill values into the current letter, filling ONLY fields the user has left
 * blank. A value the user typed (or a previously saved non-empty value) is never overwritten,
 * which is what lets the prefill keep running after the letter has been saved once with an
 * empty Scope. Returns the same object reference when nothing changed, so the caller's
 * setState is a no-op instead of a re-render.
 */
export const mergeConfirmationLetterPrefill = (current = {}, derived = {}) => {
  const filled = Object.entries(derived).reduce((acc, [field, value]) => {
    const nextValue = String(value ?? '').trim();
    if (!nextValue) return acc;
    if (String(current?.[field] ?? '').trim()) return acc;
    acc[field] = value;
    return acc;
  }, {});
  return Object.keys(filled).length === 0 ? current : { ...current, ...filled };
};
